import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Checkbox, Group, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMailHeart } from '@tabler/icons-react';
import { useState } from 'react';
import { completeMarketingOnboarding } from '../../api/marketing';
import { useUserStore } from '../../stores/userStore';
import OnboardingShell from './OnboardingShell';

const choices = [
  {
    slug: 'tgp-product-updates',
    title: 'Product Updates',
    description: 'Major new features, improvements, and beta announcements—only when there is something useful.',
  },
  {
    slug: 'tgp-tips-recommendations',
    title: 'Tips & Recommendations',
    description: 'Personalised help getting your collection, backlog, lists, and analytics working for you.',
  },
  {
    slug: 'tgp-newsletter',
    title: 'TGP Newsletter',
    description: 'Collecting advice, community stories, and occasional news—about monthly.',
  },
];
const allChoiceSlugs = choices.map((choice) => choice.slug);

export default function MarketingOnboardingPage() {
  const { getAccessTokenSilently } = useAuth0();
  const { checkUserState } = useUserStore();
  const [selected, setSelected] = useState<string[]>([]);
  const [customising, setCustomising] = useState(false);
  const [saving, setSaving] = useState(false);

  const complete = async (slugs: string[]) => {
    setSaving(true);
    try {
      await completeMarketingOnboarding(getAccessTokenSilently, slugs);
      await checkUserState(getAccessTokenSilently);
    } catch {
      notifications.show({
        title: 'Could not save email choices',
        message: 'Your choices could not be fully saved. Please review them and try again.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingShell step={2} reassurance="Everything on this step is optional and can be changed in your profile.">
      <Stack gap="xl">
        <div>
          <Group gap="md" align="center" wrap="nowrap">
            <ThemeIcon size={52} radius="md" variant="light" color="blue">
              <IconMailHeart size={29} stroke={1.8} />
            </ThemeIcon>
            <div>
              <Text c="blue.7" size="xs" fw={700} tt="uppercase" lts="0.1em" mb={5}>
                Optional email preferences
              </Text>
              <Title order={1} ff="'Sora Variable', 'Trebuchet MS', sans-serif" fz={{ base: 28, sm: 34 }}>
                Stay in the loop
              </Title>
            </div>
          </Group>
          <Text c="dimmed" mt="md" size="lg">
            Get more from TGP with practical onboarding tips and recommendations, occasional product and beta updates,
            and our monthly newsletter with collecting advice and community stories.
          </Text>
          <Text c="dimmed" mt="sm">
            You can adjust these preferences in your user settings at any time.
          </Text>
        </div>

        {customising ? (
          <>
            <Checkbox.Group value={selected} onChange={setSelected}>
              <Stack gap="sm">
                {choices.map((choice) => (
                  <Box
                    key={choice.slug}
                    p="md"
                    style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 10 }}
                  >
                    <Checkbox
                      value={choice.slug}
                      label={choice.title}
                      description={choice.description}
                      styles={{ label: { fontWeight: 600 }, description: { marginTop: 4, lineHeight: 1.45 } }}
                    />
                  </Box>
                ))}
              </Stack>
            </Checkbox.Group>

            <Group grow align="stretch">
              <Button variant="subtle" color="gray" disabled={saving} onClick={() => complete([])}>
                Not for me
              </Button>
              <Button size="md" loading={saving} onClick={() => complete(selected)}>
                Save and continue
              </Button>
            </Group>
          </>
        ) : (
          <Stack gap="sm">
            <Group grow align="stretch">
              <Button
                variant="outline"
                size="md"
                disabled={saving}
                onClick={() => {
                  setSelected(allChoiceSlugs);
                  setCustomising(true);
                }}
              >
                Customise what I receive
              </Button>
              <Button size="md" loading={saving} onClick={() => complete(allChoiceSlugs)}>
                Yes, keep me updated
              </Button>
            </Group>
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              mx="auto"
              disabled={saving}
              onClick={() => complete([])}
            >
              Not for me
            </Button>
          </Stack>
        )}
      </Stack>
    </OnboardingShell>
  );
}
